
import { Briefcase, Calendar, Info, MessageSquare, PercentIcon, User2 } from "lucide-react";
import { OpportunityWithRelations } from "@/types/custom-types";
import { formatCurrency } from "@/lib/utils";

interface OpportunityDetailsProps {
  opportunity: OpportunityWithRelations;
}

export function OpportunityDetails({ opportunity }: OpportunityDetailsProps) {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
            <Info className="h-4 w-4 mr-1" /> 
            Description
          </h3>
          <p className="text-sm">
            {opportunity.description || "No description provided"}
          </p>
        </div>
        
        {opportunity.service && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
              <Briefcase className="h-4 w-4 mr-1" /> 
              Service
            </h3>
            <div className="text-sm font-medium">
              {opportunity.service.name}
              {opportunity.service.description && (
                <p className="text-sm font-normal text-muted-foreground mt-1">
                  {opportunity.service.description}
                </p>
              )}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
            <Calendar className="h-4 w-4 mr-1" /> 
            Expected Close Date
          </h3>
          <div className="text-sm">
            {formatDate(opportunity.expected_close_date)}
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
            <PercentIcon className="h-4 w-4 mr-1" /> 
            Success Probability
          </h3>
          <div className="text-sm">
            {opportunity.probability ? `${opportunity.probability}%` : "Not specified"}
          </div>
        </div>

        {opportunity.opportunity_type && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              Opportunity Type
            </h3>
            <div className="text-sm">{opportunity.opportunity_type}</div>
          </div>
        )}
      </div>
      
      <div className="space-y-4">
        {opportunity.owner && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
              <User2 className="h-4 w-4 mr-1" /> 
              Opportunity Owner
            </h3>
            <div className="text-sm">
              {opportunity.owner.first_name} {opportunity.owner.last_name}
            </div>
          </div>
        )}
        
        {opportunity.competitor_info && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              Competitor Information
            </h3>
            <div className="text-sm">{opportunity.competitor_info}</div>
          </div>
        )}
        
        {opportunity.decision_makers && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
              <MessageSquare className="h-4 w-4 mr-1" /> 
              Decision Makers
            </h3>
            <div className="text-sm">{opportunity.decision_makers}</div>
          </div>
        )}
        
        {opportunity.win_strategy && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              Win Strategy
            </h3>
            <div className="text-sm">{opportunity.win_strategy}</div>
          </div>
        )}

        {opportunity.next_steps && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              Next Steps
            </h3>
            <div className="text-sm">{opportunity.next_steps}</div>
          </div>
        )}
        
        {(opportunity.stage === "Closed Lost" && opportunity.loss_reason) && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-1">
              Loss Reason
            </h3>
            <div className="text-sm">{opportunity.loss_reason}</div>
          </div>
        )}
      </div>
    </div>
  );
}
